import { useEffect, useState } from "react";
import api, { formatApiError, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Wallet as WalletIcon, ArrowUpRight, ArrowDownRight, Banknote, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";

export default function Wallet() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [w, p] = await Promise.all([api.get("/wallet"), api.get("/payouts")]);
    setData(w.data);
    setPayouts(p.data);
  };
  useEffect(() => { load(); }, []);

  const symbol = data?.currency_symbol || "₹";
  const balance = data?.balance || 0;
  const threshold = data?.payout_threshold || 0;
  const canWithdraw = !!data && balance >= threshold && threshold > 0;

  const submitWithdraw = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/wallet/withdraw", { amount: parseFloat(amount), method: "bank", account_details: account });
      toast.success("Payout request submitted!");
      setOpen(false); setAmount(""); setAccount("");
      await Promise.all([load(), refresh()]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Wallet</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-900 rounded-2xl p-7 text-white relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-500/10 rounded-full" />
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 text-slate-300"><WalletIcon className="w-5 h-5" /><span className="text-xs uppercase tracking-widest font-bold">Balance</span></div>
              <p className="font-display text-4xl font-extrabold text-emerald-400 mt-2" data-testid="wallet-page-balance">{money(balance, symbol)}</p>
            </div>
            <div>
              <div className="flex items-center gap-2 text-slate-300"><Gift className="w-5 h-5" /><span className="text-xs uppercase tracking-widest font-bold">Reward points</span></div>
              <p className="font-display text-4xl font-extrabold text-amber-400 mt-2" data-testid="wallet-points">{(data?.reward_points || 0).toLocaleString()}</p>
            </div>
          </div>
          <div className="mt-5">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (balance / threshold) * 100)}%` }} />
            </div>
            <p className="text-slate-400 text-sm mt-2">{canWithdraw ? "You can withdraw now!" : `${money(threshold - balance, symbol)} more to unlock payout`}</p>
          </div>
          <Button onClick={() => setOpen(true)} disabled={!canWithdraw} data-testid="withdraw-btn"
            className="mt-6 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 font-bold">
            <Banknote className="w-4 h-4 mr-2" /> Withdraw funds
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Payout requests</p>
          {payouts.length === 0 ? (
            <p className="text-slate-500 text-sm mt-4">No payout requests yet.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {payouts.slice(0, 5).map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm" data-testid={`payout-${p.id}`}>
                  <div>
                    <p className="font-semibold text-slate-900">{money(p.amount, symbol)}</p>
                    <p className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                    p.status === "approved" ? "bg-emerald-50 text-emerald-700" : p.status === "pending" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"
                  }`}>{p.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <h2 className="font-display text-xl font-bold text-slate-900 mb-4">Transaction history</h2>
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {(data?.transactions || []).length === 0 ? (
            <p className="text-slate-500 text-sm p-6">No transactions yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between px-5 py-3.5" data-testid={`tx-${t.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${t.amount >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
                      {t.amount >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{t.description}</p>
                      <p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <span className={`font-display font-bold ${t.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {t.amount >= 0 ? "+" : ""}{money(t.amount, symbol)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl">Withdraw funds</DialogTitle></DialogHeader>
          <form onSubmit={submitWithdraw} className="space-y-4">
            <div>
              <Label className="font-semibold">Amount ({symbol})</Label>
              <Input type="number" min="1" max={balance} step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="withdraw-amount" className="mt-1.5 bg-slate-50 py-5" placeholder={`Max ${money(balance, symbol)}`} />
            </div>
            <div>
              <Label className="font-semibold">Bank / UPI details</Label>
              <Input required value={account} onChange={(e) => setAccount(e.target.value)} data-testid="withdraw-account" className="mt-1.5 bg-slate-50 py-5" placeholder="Account no. or UPI ID" />
            </div>
            <Button type="submit" disabled={loading} data-testid="withdraw-submit" className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 font-bold py-6">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Request payout"}
            </Button>
            <p className="text-xs text-slate-400 text-center">Payouts are reviewed and approved by our team before processing.</p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
